import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { ReturnDDSDocument } from "app/bundles/requests/shared/models/returnDDSDocument";
import {
  from as observableFrom,
  Observable,
  of,
  Subject,
  throwError as observableThrowError,
  forkJoin,
} from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { CoreService } from "../../../../core/core.service";
import { EventApi } from "../api-services/event-api/event-api.models";
import { IMediaPayload } from "../api-services/event-api/event-api.service";
import { RequestApi } from "../api-services/request-api/request-api.models";
import { CollapseItem, CollapseItemsEnum } from "../models/collapseItem";
import { IAllowableActivity } from "../models/currentUser";
import { EventRequestById } from "../models/event";
import { IApiErrorSuccessDTO } from "../models/apiErrorSuccess";
import {
  IWorkorderOrServiceRequestDetail,
  IWorkorderOrServiceRequestSummary,
  IWorkorderRequestDetail,
  RequestSourceTypeEnum,
} from "../models/request";
import { UtilService, extractForemanMedias } from "./util.service";

@Injectable()
export class EditRequestService {
  private collapseItems: CollapseItem[] = [];
  private requestChangedNotify =
    new Subject<IWorkorderOrServiceRequestSummary>();
  public requestChangedObservable = this.requestChangedNotify.asObservable();

  constructor(
    private requestApiService: RequestApi,
    private eventApiService: EventApi,
    private coreService: CoreService,
    private router: Router,
    private utilsService: UtilService,
  ) {}

  public addCollapseItem(item: CollapseItem): void {
    this.collapseItems.push(item);
  }

  public clearAllCollapseItems(): void {
    this.collapseItems.length = 0;
  }

  public findCollapseItemByName(name: CollapseItemsEnum): CollapseItem {
    if (!name) {
      return null;
    }

    return this.collapseItems.find(
      (collapseItem) => collapseItem.getName() === name,
    );
  }

  public toggleCollapse(itemName: CollapseItemsEnum): void {
    const item = this.collapseItems.find(
      (collapseItem) => collapseItem.getName() === itemName,
    );

    if (!item) {
      return;
    }

    if (item.getCollapsed()) {
      item.setCollapsed(false);
      item.setBtnDisabled(true);
      item.setBtnVisible(false);
      item.setSessionVisible(true);
    } else {
      item.setCollapsed(true);
      item.setBtnDisabled(false);
      item.setBtnVisible(true);
      item.setSessionVisible(true);
    }

    this.collapseItems.forEach((collapseItem) => {
      if (collapseItem.getName() !== itemName) {
        if (item.getCollapsed()) {
          collapseItem.setCollapsed(true);
          collapseItem.setBtnDisabled(false);
          collapseItem.setBtnVisible(true);
          collapseItem.setSessionVisible(true);
        } else {
          collapseItem.setCollapsed(true);
          collapseItem.setBtnDisabled(true);
          collapseItem.setBtnVisible(true);
          collapseItem.setSessionVisible(false);
        }
      }
    });
  }

  public reloadRequest(requestToReload: IWorkorderOrServiceRequestSummary) {
    return this.getFormattedRequestById(
      requestToReload.id,
      requestToReload.source,
      requestToReload,
    ).pipe(
      map((requestDetail) => {
        return this.updateSummaryFromDetail(requestToReload, requestDetail);
      }),
      tap((request) => {
        this.requestChangedNotify.next(request);
      }),
    );
  }

  private updateSummaryFromDetail(
    requestToReload: IWorkorderOrServiceRequestSummary,
    requestDetail: IWorkorderOrServiceRequestDetail,
  ): IWorkorderOrServiceRequestSummary {
    const source = requestDetail.source;
    const summaryRefreshed: IWorkorderOrServiceRequestSummary = {
      status: requestDetail.status,
      estimatedCompletionDate: requestDetail.estimatedCompletionDate,
      service: requestDetail.service,
      responsible: requestDetail.responsible,
      // Properties that do not change following a reload
      isSelected: requestToReload.isSelected,
      isFiltred: requestToReload.isFiltred,
      serviceRequestId: requestToReload.serviceRequestId,
      id: requestToReload.id,
      requestNumber: requestToReload.requestNumber,
      isRelaunched: requestToReload.isRelaunched,
      isReactivated: requestToReload.isReactivated,
      description: requestToReload.description,
      formattedAddress: requestToReload.formattedAddress,
      borough: requestToReload.borough,
      geometry: requestToReload.geometry,
      creationDatetime: requestToReload.creationDatetime,
      source: RequestSourceTypeEnum[requestToReload.source.toString()],
      comments:
        requestToReload.source === RequestSourceTypeEnum.serviceRequest
          ? requestToReload.comments
          : undefined,
      events:
        requestToReload.source === RequestSourceTypeEnum.serviceRequest
          ? requestToReload.events
          : undefined,
      type:
        requestToReload.source === RequestSourceTypeEnum.workorderRequest
          ? requestToReload.type
          : undefined,
      hasExistingTask:
        source === RequestSourceTypeEnum.workorderRequest
          ? requestDetail.hasExistingTask
          : undefined,
    };
    return summaryRefreshed;
  }

  public updateStatus(event: EventRequestById): Observable<EventRequestById> {
    if (!event.requestId) {
      return observableThrowError(
        "Il faut inclure id de DDD au processus de changement des statuts",
      );
    }
    return this.updateRequest(event);
  }

  public updateRequest(event: EventRequestById): Observable<EventRequestById> {
    return this.eventApiService.addEventByWorkorderId(event.requestId, event);
  }

  public returnDDS(id: number, document: ReturnDDSDocument): Observable<any> {
    return this.requestApiService.returnDDS(id, document);
  }

  public takeInCharge(
    ids: number[],
    responsibleUser: string,
  ): Observable<IApiErrorSuccessDTO> {
    return this.eventApiService.postTakeInCharge(ids, responsibleUser);
  }

  public changeResponsible(
    ids: number[],
    responsibleUser: string,
    requestType: RequestSourceTypeEnum,
  ) {
    return this.eventApiService.postChangeResponsible(
      ids,
      responsibleUser,
      requestType,
    );
  }

  public changeActivity(
    ids: number[],
    requestType: RequestSourceTypeEnum,
    activity: IAllowableActivity,
  ): Observable<IApiErrorSuccessDTO> {
    if (requestType === RequestSourceTypeEnum.workorderRequest) {
      return this.eventApiService.postChangeActivity({
        requestIds: ids,
        requestType,
        responsibleGroupId: activity.group.id,
        activityCode: activity.code,
        activityTemplateId: activity.template.id,
        responsibleUnitId: activity.responsibleUnit.id,
      });
    }
    return this.eventApiService.postChangeActivity({
      requestIds: ids,
      requestType,
      responsibleGroupId: activity.group.id,
      activityCode: activity.code,
      responsibleUnitId: activity.responsibleUnit.id,
    });
  }

  public closeWorkorder(
    requestIds: number[],
    requestType: RequestSourceTypeEnum,
    reason: string,
    internalComment: string,
    photos: IMediaPayload[],
  ): Observable<IApiErrorSuccessDTO> {
    if (requestType !== RequestSourceTypeEnum.workorderRequest) {
      return of({});
    }
    const eventProperties = {
      requestIds,
      requestType,
      internalComment,
      reason,
      photos,
    };

    return this.eventApiService.postCloseWorkorder(eventProperties);
  }

  public getFormattedRequestById(
    id: number,
    source: RequestSourceTypeEnum,
    requestSummary: IWorkorderOrServiceRequestSummary,
  ): Observable<IWorkorderOrServiceRequestDetail> {
    const serviceRequestIdIfKnown =
      requestSummary.serviceRequestId ||
      (requestSummary.source === RequestSourceTypeEnum.serviceRequest
        ? id
        : null);
    const serviceRequestDetail$ = serviceRequestIdIfKnown
      ? this.requestApiService
          .getServiceRequestById(serviceRequestIdIfKnown)
          .pipe(
            map((serviceRequest) => {
              return this.utilsService.formatRawServiceRequestToServiceRequest(
                serviceRequest,
                requestSummary,
              );
            }),
          )
      : of(null);
    if (source === RequestSourceTypeEnum.workorderRequest) {
      return forkJoin([
        this.requestApiService.getWorkorderById(id),
        this.requestApiService.getDocumentsByWorkorderId(id),
        serviceRequestDetail$,
      ]).pipe(
        map(
          ([
            workorderRequestDetail,
            documents,
            serviceRequestDetail,
          ]): IWorkorderOrServiceRequestDetail => {
            const foremanMedias = extractForemanMedias(documents);
            return {
              ...workorderRequestDetail,
              source,
              foremanMedias,
              serviceRequestDetail,
              requestSummary,
            };
          },
        ),
      );
    } else {
      return serviceRequestDetail$;
    }
  }
}
